import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import {
  DrawerComponent,
  INSTRUCTOR_DRAWER_TAB,
  INSTRUCTOR_SETTINGS_DRAWER_TAB,
  PRODUCT_DRAWER_TAB,
  RefundDetails,
  RefundRequestStatus,
  RejectionPayload,
  STUDENT_DRAWER_TAB,
  STUDENT_SETTINGS_DRAWER_TAB,
  TabItem,
} from '@shared';
import { TransactionsService } from '@shared/services';
import { BreadcrumbService } from '@shared/services/breadcrumb/breadcrumb.service';
import * as _ from 'lodash';
import { Subscription } from 'rxjs';
import { FileProps, LANGUAGES, ModalComponent, ModalService, RouterStoreService, ToastService, UtilsService } from 'thkee-common';
import { NgTemplateOutlet, DatePipe } from '@angular/common';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { TooltipDirective } from '../../../../../../../thkee-common/src/lib/directives/tooltip.directive';
import { DrawerComponent as DrawerComponent_1 } from '../../../../@shared/components/drawer/drawer.component';
import { StudentProfileComponent } from '../../../../@shared/components/student-profile/student-profile.component';

@Component({
    selector: 'app-refund-request-details',
    templateUrl: './refund-request-details.component.html',
    styleUrls: ['./refund-request-details.component.scss'],
    imports: [NgTemplateOutlet, AngularSvgIconModule, TooltipDirective, DrawerComponent_1, FormsModule, ReactiveFormsModule, FormlyModule, StudentProfileComponent, ModalComponent, DatePipe]
})
export class RefundRequestDetailsComponent implements OnInit, OnDestroy {
  private transactionsService = inject(TransactionsService);
  private routerStore = inject(RouterStoreService);
  private breadcrumbService = inject(BreadcrumbService);
  private router = inject(Router);
  private toastService = inject(ToastService);
  private readonly modalService = inject(ModalService);
  private utilsService = inject(UtilsService);

  params: any = {};
  //TAB
  productTabLists: TabItem[] = PRODUCT_DRAWER_TAB;
  studentTabLists: TabItem[] = STUDENT_DRAWER_TAB;
  studentSettingsTabLists: TabItem[] = STUDENT_SETTINGS_DRAWER_TAB;
  instructorTabLists: TabItem[] = INSTRUCTOR_DRAWER_TAB;
  instructorSettingsTabLists: TabItem[] = INSTRUCTOR_SETTINGS_DRAWER_TAB;

  private subscriptions = new Subscription();
  refundDetails!: RefundDetails;
  isLoading: boolean = false;

  // Create product form
  productForm = new FormGroup({});
  productFormModel: any = {};
  productFormFields: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: 'card block py-9 px-6 mb-4',
      fieldGroup: [
        {
          className: 'section-label block border-b-[2px] border-neutral-200 pb-3 mb-3',
          template: '<h5>General</h5>',
        },
        {
          key: 'title',
          type: 'input',
          wrappers: ['feedback-field'],
          defaultValue: '#SAP MM Material Management',
          props: {
            label: 'Title',
            minLength: 6,
            maxLength: 60,
            placeholder: 'e.g. Learn Blender in 60 minutes',
            disabled: true,
            tips: 'This is a tip message for course title',
          },
          validation: {
            messages: {
              minLength: "Title can't be lower than 6 characters.",
            },
          },
          expressions: {},
        },
        {
          key: 'subtitle',
          type: 'input',
          wrappers: ['feedback-field'],
          defaultValue: 'Introduction to SAP MM Material Management',
          props: {
            label: 'Subtitle',
            minLength: 6,
            maxLength: 60,
            placeholder: 'e.g. Introduction to SAP MM Material Management!',
            disabled: true,
            tips: 'This is a tip message for course sub title',
          },
          validation: {
            messages: {
              minLength: "This field can't be lower than 6 characters.",
            },
          },
          expressions: {},
        },
        {
          key: 'topics',
          type: 'select',
          defaultValue: ['uiux', 'frontend'],
          wrappers: ['feedback-field'],
          props: {
            label: 'Topics',
            placeholder: 'Choose topic',
            multiple: true,
            stayPlaceholder: true,
            disabled: true,
            tips: 'This is a tip message for course title',
            stylish: true,
            options: [
              { label: 'UI/UX', value: 'uiux' },
              { label: 'Frontend', value: 'frontend' },
              { label: 'Backend', value: 'backend' },
            ],
          },
          expressions: {},
        },
        {
          fieldGroupClassName: 'grid grid-cols-2 gap-6 border-b border-neutral-200 mb-3',
          fieldGroup: [
            {
              key: 'skill_level',
              type: 'select',
              defaultValue: 'Beginner',
              wrappers: ['feedback-field'],
              props: {
                wrapAppendClass: ['border-none', '!mb-0'],
                label: 'Level',
                placeholder: 'Choose a level',
                stylish: true,
                options: [
                  { label: 'All', value: 'All' },
                  { label: 'Beginner', value: 'Beginner' },
                  { label: 'Intermediate', value: 'Intermediate' },
                  { label: 'Expert', value: 'Expert' },
                ],
                disabled: true,
                tips: 'This is a tip message for course title',
              },
              expressions: {},
            },
            {
              key: 'language',
              type: 'select',
              wrappers: ['feedback-field'],
              props: {
                wrapAppendClass: ['border-none', '!mb-0'],
                label: 'Language',
                placeholder: 'Select language',
                stylish: true,
                options: _.sortBy(
                  _.entries(LANGUAGES).map(([key, value]) => ({ label: value, value: key })),
                  (lang) => lang.label
                ),
                disabled: true,
                tips: 'This is a tip message for course title',
              },
              expressions: {},
            },
          ],
        },
        {
          fieldGroupClassName: 'grid grid-cols-2 gap-6',
          fieldGroup: [
            {
              key: 'category',
              type: 'select',
              defaultValue: 'business',
              wrappers: ['feedback-field'],
              props: {
                wrapAppendClass: ['border-none', '!mb-0'],
                label: 'Category',
                placeholder: 'Choose a category',
                stylish: true,
                disabled: true,
                tips: 'This is a tip message for course title',
                options: [
                  { label: 'Business', value: 'business' },
                  { label: 'Design', value: 'design' },
                  { label: 'Language', value: 'language' },
                ],
              },
              expressions: {},
            },
            {
              key: 'subcategory',
              type: 'select',
              defaultValue: 'graphic-design',
              wrappers: ['feedback-field'],
              props: {
                wrapAppendClass: ['border-none', '!mb-0'],
                label: 'Sub category',
                placeholder: 'Choose sub-category',
                stylish: true,
                disabled: true,
                tips: 'This is a tip message for course title',
                options: [
                  { label: 'Graphic Design', value: 'graphic-design' },
                  { label: 'Web Design', value: 'web-design' },
                  { label: 'UI/UX Design', value: 'uiux-design' },
                ],
              },
              expressions: {},
            },
          ],
        },
      ],
    },

    {
      className: 'block border-t border-neutral-100 card mb-4 pt-6 px-6',
      key: 'image',
      type: 'file',
      // wrappers: ['feedback-field'],
      props: {
        wrapAppendClass: ['border-none'],
        label: 'Image Cover',
        placeholder: 'Upload your course image here. Supported files are .jpg, .jpeg, or .png.',
        preview: true,
        previewType: 'image',
        allowedTypes: ['image/png', 'image/jpg', 'image/jpeg', 'image/webp'],
        uploadType: 'dashboard',
        disabled: false,
        tips: 'This is a tip message for course title',
        acceptFileType: '.webp, .jpg, .jpeg, or .png',
      } as FileProps,
      expressions: {},
    },
    {
      className: 'block border-t border-neutral-100 card mb-4 pt-6 px-6',
      key: 'promo_video',
      type: 'file',
      // wrappers: ['feedback-field'],
      props: {
        wrapAppendClass: ['border-none'],
        label: 'Promotional Video',
        placeholder: "Upload your course's promotional video here. Maximum of 200MB.",
        preview: true,
        previewType: 'video',
        hideLabel: false,
        allowedTypes: ['video/mp4', 'video/mkv', 'video/mov'],
        uploadType: 'dashboard',
        disabled: false,
        tips: 'This is a tip message for course title',
        onUpload: (upload, field) => {
          // log.debug('upload: ', upload);
          // firstValueFrom(this.courseId$).then((courseId) => {
          //   this.store.dispatch(
          //     CourseActions.uploadStart({
          //       referenceId: `${courseId}-${field.key}`,
          //       fileName: upload.name,
          //       fileType: upload.type,
          //     })
          //   );
          // });
        },
        onProgress: (progress, field) => {
          // log.debug('progress: ', progress);
          // firstValueFrom(this.courseId$).then((courseId) => {
          //   this.store.dispatch(
          //     CourseActions.uploadProgress({
          //       referenceId: `${courseId}-${field.key}`,
          //       bytesTotal: progress.bytesTotal,
          //       bytesUploaded: progress.bytesUploaded,
          //       progressPercent: progress.progressPercent,
          //     })
          //   );
          // });
        },
        onComplete: (field) => {
          // log.debug('complete');
          // firstValueFrom(this.courseId$).then((courseId) => {
          //   this.store.dispatch(
          //     CourseActions.uploadComplete({
          //       referenceId: `${courseId}-${field.key}`,
          //     })
          //   );
          // });
        },
      } as FileProps,
      expressions: {
        // 'props.metadata': combineLatest([this.course$, this.userId$]).pipe(
        //   map(([course, userId]) => ({
        //     user: userId,
        //     course: course.id,
        //   })),
        //   distinctUntilChanged()
        // ),
        // 'props.previewUrl': this.course$.pipe(
        //   map((course) => {
        //     return course.promo_video_url;
        //   }),
        //   distinctUntilChanged()
        // ),
        // 'props.feedback': this.getFeedbackCount('promo_video'),
      },
    },
  ];

  // Rejection Reason form
  rejectReasonForm = new FormGroup({});
  rejectReasonFormModel = { reason: 'Reason1', description: '' };
  rejectReasonFormFields: FormlyFieldConfig[] = [
    {
      key: 'reason',
      type: 'select',
      defaultValue: ['Outdated Course Material'],
      props: {
        label: $localize`Reason`,
        placeholder: $localize`Choose topic`,
        multiple: false,
        stayPlaceholder: true,
        tips: $localize`Refund reason`,
        stylish: true,
        options: [
          { value: 'Outdated Course Material', label: $localize`Outdated Course Material` },
          { value: 'Instructor Performance Issues', label: $localize`Instructor Performance Issues` },
          { value: 'Course Too Difficult', label: $localize`Course Too Difficult` },
          { value: 'Course Too Easy', label: $localize`Course Too Easy` },
          { value: 'Course Not Needed', label: $localize`Course Not Needed` },
          { value: 'Technical Issues', label: $localize`Technical Issues` },
          { value: 'Unauthorized Transaction', label: $localize`Unauthorized Transaction` },
          { value: 'Better Price Found', label: $localize`Better Price Found` },
          { value: 'Incorrect Charge', label: $localize`Incorrect Charge` },
          { value: 'Duplicate Charge', label: $localize`Duplicate Charge` },
          { value: 'Exceptional Circumstances', label: $localize`Exceptional Circumstances` },
        ],
      },
      expressions: {},
    },
    {
      key: 'description',
      type: 'textarea',
      props: {
        label: $localize`Description`,
        labelClass: 'font-bold text-black text-lg pb-2.5',
        tips: $localize`Reason description`,
        placeholder: $localize`Enter a description...`,
        required: true,
        minHeight: '100px',
        maxHeight: '150px',
      },
    },
  ];

  private refundSubscriptions = new Subscription();

  ngOnInit(): void {
    this.routerStore.getParams().subscribe((params) => {
      this.params = params;
      let breadcrumb: any = [
        {
          label: 'E-Commerce',
          url: '/ecommerce/dashboard',
        },
        {
          label: 'Refund Request',
          url: '/ecommerce/refund',
        },
        {
          label: `Order #${params['orderId']}`,
          url: '',
        },
      ];
      this.breadcrumbService.setBreadcrumbs(breadcrumb);
    });

    this.getRefundDetails();
  }

  // Get data
  private getRefundDetails() {
    this.isLoading = true;
    this.subscriptions.add(
      this.transactionsService.getRefundDetails(this.params['orderId']).subscribe({
        next: (res: RefundDetails) => {
          this.refundDetails = res;
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
        },
      })
    );
  }

  @ViewChild('productDrawer') private productDrawer!: DrawerComponent;
  @ViewChild('buyerDrawer') private buyerDrawer!: DrawerComponent;
  @ViewChild('instructorDrawer') private instructorDrawer!: DrawerComponent;
  showDrawer(type: any, event: any) {
    event.stopPropagation();
    if (type === 'product') {
      this.productDrawer.open();
    }
    if (type === 'buyer') {
      this.buyerDrawer.open();
    }
    if (type === 'instructor') {
      this.instructorDrawer.open();
    }
  }
  // Refund confirm modal
  @ViewChild('refundConfirmed') refundConfirmed!: ModalComponent;
  confirmRefund() {
    this.refundConfirmed.close();
    this.refundSubscriptions.add(
      this.transactionsService.refundRequestApproved(this.params['orderId']).subscribe({
        next: (res: RefundRequestStatus) => {
          this.toastService.message({
            message: res.status,
          });
          setTimeout(() => {
            window.location.replace('/ecommerce/refund');
          }, 1000);
        },
        error: (error) => {
          this.toastService.error({
            message: error.error.status,
          });
        },
      })
    );
  }

  // Refund
  refundConfirmation() {
    this.modalService.confirm({
      title: 'Refund Confirmation',
      message: `Are you sure?`,
      onConfirm: () => {
        this.refund(this.params['orderId']);
      },
    });
  }

  private refund(refundId: string) {
    this.refundSubscriptions.add(
      this.transactionsService.refundRequestForceApproved(refundId).subscribe({
        next: (res: RefundRequestStatus) => {
          this.toastService.message({
            message: res.status,
          });
          setTimeout(() => {
            window.location.replace('/ecommerce/refund');
          }, 1000);
        },
        error: (error) => {
          this.toastService.error({
            message: error.error.status,
          });
        },
      })
    );
  }

  // Rejection confirm modal
  @ViewChild('rejectionConfirmed') rejectionConfirmed: any;
  confirmRejection() {
    this.rejectionConfirmed.close();
    this.refundSubscriptions.add(
      this.transactionsService
        .rejectRefundRequest(this.params['orderId'], this.rejectReasonForm.value as RejectionPayload)
        .subscribe({
          next: (res: RefundRequestStatus) => {
            this.toastService.message({
              message: res.status,
            });
            setTimeout(() => {
              window.location.replace('/ecommerce/refund');
            }, 1000);
          },
          error: (error) => {
            this.toastService.error({
              message: error.error.status,
            });
          },
        })
    );
  }

  // Color tag
  getStatusTagColor(status: string): string {
    if (this.utilsService.isStatus(status)) {
      return this.utilsService.getStatusColor(status);
    }
    return 'green';
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.refundSubscriptions.unsubscribe();
  }
}
